(() => {
  if (window.__tomoCampaignCartRuntime) return;
  window.__tomoCampaignCartRuntime = true;

  const DRAWER_SELECTOR = '[data-tomo-sitewide-cart]';
  const HEADER_TRIGGER_SELECTOR = '[data-testid="cart-drawer-trigger"], #shopify-section-header a[href$="/cart"], #shopify-section-header a[href*="/cart?"], header a[href$="/cart"], header a[href*="/cart?"], .header a[href$="/cart"], .header a[href*="/cart?"]';
  const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const SOURCE = 'tomo-designer-campaign';
  const currency = document.documentElement.dataset.currency || window.Shopify?.currency?.active || 'USD';
  const money = new Intl.NumberFormat('en-US', { style: 'currency', currency });
  let currentDrawer = null;
  let currentAbort = null;
  let returnFocus = null;
  let highlightTimer = 0;
  let addRefreshVersion = 0;
  let addRefreshAbort = null;
  let drawerRefreshVersion = 0;
  let drawerRefreshAbort = null;
  let cartStateVersion = 0;
  let cartMutationAbort = null;

  const liveStatus = () => document.querySelector('[data-tomo-cart-status]');

  const announce = (message) => {
    const status = liveStatus();
    if (!status) return;
    status.textContent = '';
    window.requestAnimationFrame(() => { status.textContent = message; });
  };

  const syncHeaderCount = (count) => {
    if (!Number.isFinite(count)) return;
    document.querySelectorAll('cart-icon').forEach((icon) => {
      const bubble = icon.querySelector('[ref="cartBubble"]');
      const countNode = icon.querySelector('[ref="cartBubbleCount"]');
      if (countNode) countNode.textContent = count < 100 ? String(count) : '';
      bubble?.classList.toggle('visually-hidden', count === 0);
      countNode?.classList.toggle('hidden', count === 0);
      icon.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
    });
    document.querySelectorAll('.cartCountBadge').forEach((countNode) => {
      countNode.textContent = count < 100 ? String(count) : '';
      countNode.classList.toggle('hidden', count === 0);
      countNode.setAttribute('aria-hidden', String(count === 0));
    });
  };

  const highlightHeader = () => {
    const targets = document.querySelectorAll('[data-testid="cart-drawer-trigger"], .header-actions__cart-icon, header a[href$="/cart"], .header a[href$="/cart"]');
    targets.forEach((target) => target.classList.add('tomo-cart-feedback--active'));
    window.clearTimeout(highlightTimer);
    highlightTimer = window.setTimeout(() => {
      targets.forEach((target) => target.classList.remove('tomo-cart-feedback--active'));
    }, 2400);
  };

  const cartParts = (drawer = currentDrawer) => ({
    items: drawer?.querySelector('[data-tomo-cart-items]'),
    total: drawer?.querySelector('[data-tomo-cart-total]'),
    error: drawer?.querySelector('[data-tomo-cart-error]'),
    panel: drawer?.querySelector('[data-tomo-cart-panel]'),
  });

  const setBusy = (busy) => {
    const { items } = cartParts();
    if (!items) return;
    items.setAttribute('aria-busy', String(busy));
    items.querySelectorAll('button, select').forEach((control) => { control.disabled = busy; });
  };

  const showError = (message, retry) => {
    const { error } = cartParts();
    if (!error) return;
    error.replaceChildren();
    const text = document.createElement('span');
    text.textContent = message;
    error.appendChild(text);
    if (retry) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tomo-fsv3-cart__retry';
      button.textContent = 'Try again';
      button.addEventListener('click', retry, { once: true });
      error.append(' ', button);
    }
  };

  const clearError = () => {
    const { error } = cartParts();
    error?.replaceChildren();
  };

  const productPrices = new Map();
  let renderedCart = null;
  const enrichPrices = (cart) => {
    const pending = [];
    for (const item of cart.items || []) {
      if (!item.handle) continue;
      if (!productPrices.has(item.handle)) {
        const root = window.Shopify?.routes?.root || '/';
        const request = fetch(root + 'products/' + encodeURIComponent(item.handle) + '.js', { headers: { Accept: 'application/json' } })
          .then(response => response.ok ? response.json() : null)
          .then(product => { productPrices.set(item.handle, product); return product; })
          .catch(() => { productPrices.set(item.handle, null); });
        productPrices.set(item.handle, request);
      }
      const product = productPrices.get(item.handle);
      if (product instanceof Promise) pending.push(product);
      else {
        const variant = product?.variants?.find(variant => String(variant.id) === String(item.variant_id));
        item.tomo_compare_at_price = Number(variant?.compare_at_price || 0);
      }
    }
    if (pending.length) Promise.all(pending).then(() => {
      if (renderedCart === cart && currentDrawer?.isConnected && !currentDrawer.hidden) renderCart(cart);
    });
  };
  const renderCart = (cart) => {
    renderedCart = cart;
    enrichPrices(cart);
    window.TomoRewardProgress?.update(cart);
    document.dispatchEvent(new CustomEvent('tomo:cart-rendered', { detail: { cart } }));
    const { items, total } = cartParts();
    if (!items || !total) return;
    items.replaceChildren();
    items.setAttribute('aria-busy', 'false');
    total.textContent = money.format((cart.total_price || 0) / 100);
    clearError();

    if (!Array.isArray(cart.items) || cart.items.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'tomo-fsv3-cart__empty';
      empty.textContent = 'Your cart is empty.';
      items.appendChild(empty);
      return;
    }

    cart.items.forEach((item) => {
      const row = document.createElement('article');
      row.dataset.cartKey = item.key;
      row.dataset.cartQuantity = String(item.quantity);
      row.tomoCartItem = item;

      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = '';
        image.width = 72;
        image.height = 72;
        image.loading = 'lazy';
        row.appendChild(image);
      }

      const copy = document.createElement('div');
      copy.className = 'tomo-fsv3-cart__item-copy';
      const title = document.createElement('strong');
      title.textContent = item.product_title || item.title;
      const variant = document.createElement('span');
      variant.textContent = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      const meta = document.createElement('p');
      meta.className = 'tomo-fsv3-cart__line-price';
      const originalLinePrice = Math.max(Number(item.original_line_price ?? item.final_line_price ?? 0), Number(item.tomo_compare_at_price || 0) * Number(item.quantity || 0));
      const finalLinePrice = Number(item.final_line_price ?? 0);
      if (originalLinePrice > finalLinePrice) {
        const originalPrice = document.createElement('del');
        originalPrice.className = 'tomo-fsv3-cart__original-price';
        originalPrice.textContent = money.format(originalLinePrice / 100);
        const discountedPrice = document.createElement('span');
        discountedPrice.className = 'tomo-fsv3-cart__discounted-price';
        discountedPrice.textContent = money.format(finalLinePrice / 100);
        const discountPercent = Math.round(((originalLinePrice - finalLinePrice) / originalLinePrice) * 100);
        const discountBadge = document.createElement('span');
        discountBadge.className = 'tomo-fsv3-cart__discount-percent';
        discountBadge.textContent = 'Save ' + money.format((originalLinePrice - finalLinePrice) / 100) + ' (' + String(discountPercent) + '%)';
        discountBadge.setAttribute('aria-label', String(discountPercent) + ' percent off');
        meta.append(originalPrice, discountedPrice, discountBadge);
      } else {
        meta.textContent = money.format(finalLinePrice / 100);
      }

      const controls = document.createElement('div');
      controls.className = 'tomo-fsv3-cart__quantity';
      const decrease = document.createElement('button');
      decrease.type = 'button';
      decrease.dataset.tomoCartAction = 'decrease';
      decrease.textContent = '−';
      decrease.setAttribute('aria-label', `Decrease quantity of ${item.product_title || item.title}`);
      const quantity = document.createElement('span');
      quantity.textContent = String(item.quantity);
      quantity.setAttribute('aria-label', `Quantity ${item.quantity}`);
      const increase = document.createElement('button');
      increase.type = 'button';
      increase.dataset.tomoCartAction = 'increase';
      increase.textContent = '+';
      increase.setAttribute('aria-label', `Increase quantity of ${item.product_title || item.title}`);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'tomo-fsv3-cart__remove';
      remove.dataset.tomoCartAction = 'remove';
      remove.textContent = 'Remove';
      remove.setAttribute('aria-label', `Remove ${item.product_title || item.title} from cart`);
      controls.append(decrease, quantity, increase, remove);
      copy.append(title, variant, meta, controls);
      row.appendChild(copy);
      items.appendChild(row);
    });
  };

  const fetchCart = async (signal) => {
    const url = currentDrawer?.dataset.cartJsonUrl;
    if (!url) throw new Error('Cart route is unavailable.');
    const response = await fetch(url, { headers: { Accept: 'application/json' }, signal });
    if (!response.ok) throw new Error('Your cart could not be refreshed.');
    return response.json();
  };

  const publishCartUpdate = (cart) => {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: 'tomo-campaign-mini-cart',
        data: { resource: cart, source: SOURCE, itemCount: Number(cart.item_count || 0) },
      },
    }));
    queueMicrotask(() => syncHeaderCount(Number(cart.item_count || 0)));
  };

  const closeNativeDrawer = () => {
    const nativeDrawer = document.querySelector('cart-drawer-component');
    if (nativeDrawer?.hasAttribute('auto-open')) nativeDrawer.removeAttribute('auto-open');
    const dialog = nativeDrawer?.querySelector('[data-testid="cart-drawer-dialog"]');
    if (dialog?.open && typeof nativeDrawer.close === 'function') nativeDrawer.close();
  };

  const normalizeFocusTarget = (origin, productId) => {
    if (origin instanceof HTMLElement) {
      if (origin.matches(FOCUSABLE_SELECTOR)) return origin;
      const nested = origin.querySelector?.(FOCUSABLE_SELECTOR);
      if (nested instanceof HTMLElement) return nested;
      const parent = origin.closest?.(FOCUSABLE_SELECTOR);
      if (parent instanceof HTMLElement) return parent;
    }
    if (productId) {
      const selector = `.tomo-campaign-card[data-campaign-product-id="${CSS.escape(String(productId))}"] .quick-add__button:not([disabled])`;
      const cardControl = document.querySelector(selector);
      if (cardControl instanceof HTMLElement) return cardControl;
    }
    const headerTrigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
    return headerTrigger instanceof HTMLElement ? headerTrigger : null;
  };

  const showCartShell = (origin, productId) => {
    if (!currentDrawer?.isConnected) return false;
    returnFocus = normalizeFocusTarget(origin, productId);
    closeNativeDrawer();
    currentDrawer.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    const { panel } = cartParts();
    setBusy(true);
    panel?.focus({ preventScroll: true });
    return true;
  };

  const openCart = async (knownCart, origin = document.activeElement) => {
    if (!showCartShell(origin)) return null;
    const stateVersion = ++cartStateVersion;
    addRefreshAbort?.abort();
    cartMutationAbort?.abort();
    const version = ++drawerRefreshVersion;
    drawerRefreshAbort?.abort();
    drawerRefreshAbort = new AbortController();
    const { items } = cartParts();
    try {
      const cart = knownCart?.items ? knownCart : await fetchCart(drawerRefreshAbort.signal);
      if (stateVersion !== cartStateVersion || version !== drawerRefreshVersion || !currentDrawer?.isConnected) return null;
      renderCart(cart);
      queueMicrotask(() => syncHeaderCount(Number(cart.item_count || 0)));
      return cart;
    } catch (error) {
      if (error.name === 'AbortError') return null;
      if (items) items.setAttribute('aria-busy', 'false');
      showError(error.message || 'Your cart could not be refreshed.', () => openCart(null, returnFocus));
      return null;
    }
  };

  const refreshAfterAdd = async (origin, productId) => {
    if (!showCartShell(origin, productId)) return;
    const stateVersion = ++cartStateVersion;
    drawerRefreshAbort?.abort();
    cartMutationAbort?.abort();
    const version = ++addRefreshVersion;
    addRefreshAbort?.abort();
    addRefreshAbort = new AbortController();
    const { items } = cartParts();
    try {
      const cart = await fetchCart(addRefreshAbort.signal);
      if (stateVersion !== cartStateVersion || version !== addRefreshVersion || !currentDrawer?.isConnected) return;
      renderCart(cart);
      const total = Number(cart.item_count || 0);
      queueMicrotask(() => syncHeaderCount(total));
      highlightHeader();
      announce(`Added to cart. ${total} items in cart.`);
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (items) items.setAttribute('aria-busy', 'false');
      showError(error.message || 'Your cart could not be refreshed.', () => refreshAfterAdd(returnFocus, productId));
    }
  };

  const closeCart = () => {
    if (!currentDrawer) return;
    cartStateVersion += 1;
    currentDrawer.hidden = true;
    document.documentElement.style.overflow = '';
    addRefreshAbort?.abort();
    drawerRefreshAbort?.abort();
    cartMutationAbort?.abort();
    const focusTarget = returnFocus;
    returnFocus = null;
    if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
  };

  const changeCart = async (key, quantity) => {
    if (!key) return;
    const stateVersion = ++cartStateVersion;
    addRefreshAbort?.abort();
    drawerRefreshAbort?.abort();
    cartMutationAbort?.abort();
    cartMutationAbort = new AbortController();
    setBusy(true);
    clearError();
    try {
      const url = currentDrawer?.dataset.cartChangeUrl;
      if (!url) throw new Error('Cart update route is unavailable.');
      const response = await fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) }),
        signal: cartMutationAbort.signal,
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || 'Unable to update your cart.');
      if (stateVersion !== cartStateVersion || !currentDrawer?.isConnected) return;
      renderCart(cart);
      publishCartUpdate(cart);
      announce(`Cart updated. ${cart.item_count || 0} items in cart.`);
    } catch (error) {
      if (error.name === 'AbortError') return;
      setBusy(false);
      showError(error.message || 'Unable to update your cart.', () => changeCart(key, quantity));
    }
  };

  const trapFocus = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeCart();
      return;
    }
    if (event.key !== 'Tab') return;
    const { panel } = cartParts();
    const focusable = panel ? [...panel.querySelectorAll(FOCUSABLE_SELECTOR)].filter((node) => !node.hidden) : [];
    if (!focusable.length) {
      event.preventDefault();
      panel?.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (document.activeElement === panel) {
      event.preventDefault();
      (event.shiftKey ? last : first)?.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const onCartUpdate = (event) => {
    const detail = event.detail || {};
    const data = detail.data || {};
    if (data.source === SOURCE || data.didError) return;
    const isProductAdd = data.source === 'product-form-component';
    if (!isProductAdd) {
      if (currentDrawer && !currentDrawer.hidden) {
        const resource = detail.resource?.items ? detail.resource : null;
        if (resource) renderCart(resource);
        else {
          const stateVersion = ++cartStateVersion;
          addRefreshAbort?.abort();
          cartMutationAbort?.abort();
          const version = ++drawerRefreshVersion;
          drawerRefreshAbort?.abort();
          drawerRefreshAbort = new AbortController();
          fetchCart(drawerRefreshAbort.signal)
            .then((cart) => { if (stateVersion === cartStateVersion && version === drawerRefreshVersion) renderCart(cart); })
            .catch((error) => { if (error.name !== 'AbortError') showError('Your cart could not be refreshed.'); });
        }
      }
      return;
    }
    const activeOrigin = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    refreshAfterAdd(activeOrigin || (event.target instanceof HTMLElement ? event.target : null), data.productId);
  };

  const initDrawer = (drawer) => {
    if (!(drawer instanceof HTMLElement) || drawer.dataset.tomoCartReady === 'true') return;
    currentAbort?.abort();
    currentAbort = new AbortController();
    const { signal } = currentAbort;
    currentDrawer = drawer;
    drawer.dataset.tomoCartReady = 'true';
    closeNativeDrawer();

    drawer.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[data-tomo-cart-close]')) {
        event.preventDefault();
        closeCart();
        return;
      }
      const actionButton = target?.closest('[data-tomo-cart-action]');
      const row = actionButton?.closest('[data-cart-key]');
      if (!actionButton || !row) return;
      const currentQuantity = Number(row.dataset.cartQuantity || 0);
      const action = actionButton.dataset.tomoCartAction;
      const quantity = action === 'remove' ? 0 : action === 'increase' ? currentQuantity + 1 : currentQuantity - 1;
      changeCart(row.dataset.cartKey, quantity);
    }, { signal });

    drawer.addEventListener('keydown', trapFocus, { signal });
  };

  document.addEventListener('cart:update', onCartUpdate);
  document.addEventListener('tomo:cart-updated', (event) => {
    const cart = event.detail?.cart || event.detail;
    if (!cart?.items) return;
    syncHeaderCount(Number(cart.item_count || 0));
    if (event.detail?.open) openCart(cart);
    else if (currentDrawer && !currentDrawer.hidden) renderCart(cart);
  });
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    let trigger = target?.closest(HEADER_TRIGGER_SELECTOR);
    const link = target?.closest('a[href]');
    if (!trigger && link && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      const url = new URL(link.href, location.href);
      if (url.origin === location.origin && /\/cart\/?$/.test(url.pathname)) trigger = link;
    }
    if (!trigger || trigger.closest(DRAWER_SELECTOR) || !currentDrawer?.isConnected) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openCart(null, trigger);
  }, true);

  const reconnect = () => {
    const drawer = document.querySelector(DRAWER_SELECTOR);
    if (drawer && drawer !== currentDrawer) initDrawer(drawer);
    if (currentDrawer && !currentDrawer.isConnected) {
      currentAbort?.abort();
      addRefreshAbort?.abort();
      drawerRefreshAbort?.abort();
      cartMutationAbort?.abort();
      currentDrawer = null;
    }
  };
  new MutationObserver(reconnect).observe(document.documentElement, { childList: true, subtree: true });
  reconnect();
  const cartRoute = (window.Shopify?.routes?.root || '/') + 'cart';
  if (location.pathname.replace(/\/$/, '') === cartRoute.replace(/\/$/, '')) openCart();
})();
